package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import kotlin.math.roundToInt
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * How far a running job has got, in the same twenty cells [LProgressBar] draws.
 *
 * Deliberately the same instrument, because a reader who has learned to read one row of cells as
 * "how much of this is finished" should not have to learn a second. What differs is the fact being
 * counted: [LProgressBar] plots the *state of a locale* (approved, translated, empty) and never
 * changes on its own; this plots the *state of a run* — a pre-translation, a batch approval — which
 * moves while it is being looked at and can fail part-way through.
 *
 * **The failures get their own cells rather than a sentence underneath.** A run that translated 208
 * of 210 strings and rejected two is not a successful run, and a bar that draws it as full is the
 * bar lying about the thing somebody is watching it for. Two red cells at the end cost nothing to
 * read and cannot be missed.
 *
 * **Nothing here animates, and that is the whole design.** A determinate run repolls every few
 * seconds and each poll moves the boundary between cells; a transition on the width of anything
 * would turn a discrete readout into a creeping one and invite the eye back to it on every tick.
 * Cells change colour and nothing else — the same rule [LProgressCellStyle] states for the same
 * reason. [indeterminate] draws a static hairline for exactly this reason too: a looping shimmer is
 * the shape most products reach for and it is a moving element beside work somebody is doing.
 */
@Composable
fun LTaskProgress(
    done: Int,
    total: Int,
    modifier: Modifier = Modifier,
    /** Items the run reached and could not finish. Drawn `danger`, at the end of the row. */
    failed: Int = 0,
    label: String = t("ds.ds.ltask_progress.run_progress", "Run progress"),
    /**
     * The run has started and the server has not said how much of it there is yet.
     *
     * A static hairline plus `aria-busy`, never a loop. The honest statement at this moment is
     * "something is happening and its size is unknown", and the cells cannot say that — twenty
     * empty cells read as "nothing done out of twenty", which is a claim about a total nobody has.
     */
    indeterminate: Boolean = false,
    /**
     * One line under the cells: the locale, the provider, whatever names *this* run.
     *
     * Not the counts. Those are already in `aria-valuetext`, and beside the bar they belong in the
     * caller's own words, where they can be part of a sentence rather than a caption.
     */
    detail: String? = null,
    /**
     * The run has stopped, whatever the counts say.
     *
     * `aria-busy` is otherwise inferred from the arithmetic — done plus failed against the total —
     * and that is a claim about a job this component is never told the state of. A run cancelled at
     * 100 of 210, or one the server failed outright, leaves the counts short forever, so the bar
     * beside a badge reading "Stopped" went on announcing `aria-busy="true"`: a screen-reader
     * listener is told work is still in progress and waits for an update that will never arrive.
     * The caller knows the job state; this is where it says so.
     */
    complete: Boolean = false,
) {
    val safeTotal = total.coerceAtLeast(0)
    val safeDone = done.coerceIn(0, safeTotal)
    val safeFailed = failed.coerceIn(0, safeTotal)
    val valueText = if (safeFailed > 0) {
        tf(
            "ds.ds.ltask_progress.done_of_total_done_failed_failed",
            "{done} of {total} done, {failed} failed",
            "done" to safeDone,
            "total" to safeTotal,
            "failed" to safeFailed,
        )
    } else {
        tf(
            "ds.ds.ltask_progress.done_of_total_done",
            "{done} of {total} done",
            "done" to safeDone,
            "total" to safeTotal,
        )
    }
    val startingText = t("ds.ds.ltask_progress.starting", "Starting")

    Div(
        LTaskProgressStyle.toModifier()
            .then(modifier)
            .toAttrs {
                attr("role", "progressbar")
                attr("aria-label", label)
                if (indeterminate) {
                    // No `aria-valuenow`: its absence is how ARIA spells "indeterminate", and a
                    // number invented to fill the slot would be a number a screen reader reads out.
                    attr("aria-busy", (!complete).toString())
                    attr("aria-valuetext", startingText)
                } else {
                    attr("aria-busy", (!complete && safeDone + safeFailed < safeTotal).toString())
                    attr("aria-valuemin", "0")
                    attr("aria-valuemax", safeTotal.toString())
                    attr("aria-valuenow", safeDone.toString())
                    attr("aria-valuetext", valueText)
                }
            },
    ) {
        if (indeterminate) {
            Div(LTaskProgressHairlineStyle.toModifier().toAttrs { attr("aria-hidden", "true") })
        } else {
            Div(LProgressCellsStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
                taskProgressCells(safeDone, safeFailed, safeTotal).forEach { state ->
                    Div(
                        LProgressCellStyle.toModifier()
                            .then(
                                when (state) {
                                    LTaskCellState.EMPTY -> LProgressEmptyCellStyle.toModifier()
                                    LTaskCellState.DONE -> LTaskProgressDoneCellStyle.toModifier()
                                    LTaskCellState.FAILED -> LTaskProgressFailedCellStyle.toModifier()
                                },
                            )
                            .toAttrs { attr("data-task-cell", state.attributeValue) },
                    )
                }
            }
        }
        if (detail != null) {
            Span(LTaskProgressDetailStyle.toModifier().toAttrs()) { Text(detail) }
        }
    }
}

val LTaskProgressStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", Space.SM.toString())
        }
}

/**
 * What "started, size unknown" looks like: a line, sitting still.
 *
 * Two pixels rather than a cell's sixteen, so the row it will become is visibly not there yet and
 * the surface does not jump by fourteen pixels when the first count arrives — the hairline occupies
 * the same band the cells will.
 */
val LTaskProgressHairlineStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .height(2.px)
        .borderRadius(Radius.Pill)
        .backgroundColor(ColorTokens.BorderStrong.value())
}

/** Finished. The same green [LProgressApprovedCellStyle] draws, from the same one lookup. */
val LTaskProgressDoneCellStyle = CssStyle.base {
    Modifier
        .backgroundColor(ChartPalette.forState(colorMode, ChartState.APPROVED))
        .border(1.px, LineStyle.Solid, ChartPalette.forState(colorMode, ChartState.APPROVED))
}

/** Reached and refused. `BLOCKED` is the state rail's word for `danger`. */
val LTaskProgressFailedCellStyle = CssStyle.base {
    Modifier
        .backgroundColor(ChartPalette.forState(colorMode, ChartState.BLOCKED))
        .border(1.px, LineStyle.Solid, ChartPalette.forState(colorMode, ChartState.BLOCKED))
}

val LTaskProgressDetailStyle = CssStyle.base {
    Modifier
        .color(ColorTokens.TextSecondary.value())
        .fontSize(TypeScale.Small)
        .tabularNumbers()
        .styleModifier { property("line-height", TypeScale.BodyLineHeight.toString()) }
}

internal enum class LTaskCellState(val attributeValue: String) {
    DONE("done"),
    FAILED("failed"),
    EMPTY("empty"),
}

private const val TASK_CELL_COUNT = 20

/**
 * Twenty cells from three counts.
 *
 * **A non-zero count never rounds away to nothing.** Two failures out of two hundred and ten is
 * 0.19 of a cell, and the arithmetic answer — draw none — hides the one fact the reader came for.
 * So any count above zero claims at least one cell, and the failures claim theirs first: a run's
 * failures are the part of it a person has to act on, and a full-looking bar with the red squeezed
 * out is the defect this exists to prevent.
 */
internal fun taskProgressCells(done: Int, failed: Int, total: Int): List<LTaskCellState> {
    if (total <= 0) return List(TASK_CELL_COUNT) { LTaskCellState.EMPTY }

    val failedCells = cellsFor(failed, total).coerceAtMost(TASK_CELL_COUNT)
    val doneCells = cellsFor(done, total).coerceAtMost(TASK_CELL_COUNT - failedCells)

    return List(TASK_CELL_COUNT) { index ->
        when {
            index < doneCells -> LTaskCellState.DONE
            index < doneCells + failedCells -> LTaskCellState.FAILED
            else -> LTaskCellState.EMPTY
        }
    }
}

private fun cellsFor(count: Int, total: Int): Int {
    if (count <= 0) return 0
    return (count * TASK_CELL_COUNT.toDouble() / total).roundToInt().coerceAtLeast(1)
}
